import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LoaderService {

  private loaderSubject = new Subject<LoaderState>();
  private lazyloaderSubject = new Subject<LazyLoaderState>();
  loaderState = this.loaderSubject.asObservable();
  lazyloaderState = this.lazyloaderSubject.asObservable();
  constructor() { }

  /**
   * Sticky message that survives intermediate hide() calls.
   * Set with setPersistentMessage(), cleared with clearPersistentMessage().
   * Every subsequent show() (with or without a one-off message arg) will
   * display this message until it is explicitly cleared.
   */
  private persistentMessage: string = '';
  private persistentSubtitle: string = '';

  /** Optional progress counter shown between message and subtitle. */
  private progressCurrent: number | null = null;
  private progressTotal: number | null = null;

  setPersistentMessage(msg: string, subtitle?: string): void {
    this.persistentMessage = msg;
    this.persistentSubtitle = subtitle || '';
  }

  clearPersistentMessage(): void {
    this.persistentMessage = '';
    this.persistentSubtitle = '';
  }

  /**
   * Set a live progress counter displayed as "current / total" between the
   * main message and the subtitle.  Call with (null, null) or clearProgress()
   * to remove the counter.
   */
  setProgress(current: number | null, total: number | null): void {
    this.progressCurrent = current;
    this.progressTotal = total;
    // Re-emit the current show state so the component picks up the new values.
    const effective = this.persistentMessage || '';
    this.loaderSubject.next(<LoaderState>{
      show: true,
      message: effective,
      subtitle: this.persistentSubtitle || '',
      progressCurrent: this.progressCurrent,
      progressTotal: this.progressTotal
    });
  }

  /** Convenience: remove the progress counter. */
  clearProgress(): void {
    this.setProgress(null, null);
  }

  show(message?: string, subtitle?: string) {
    // Persistent message always wins. One-off message arg is used only when
    // no persistent message is set (preserves existing behaviour for all
    // non-huddle callers that never call setPersistentMessage).
    const effective = this.persistentMessage || message || '';
    this.loaderSubject.next(<LoaderState>{
      show: true,
      message: effective,
      subtitle: this.persistentSubtitle || subtitle || '',
      progressCurrent: this.progressCurrent,
      progressTotal: this.progressTotal
    });
  }

  hide() {
    // While a persistent message is active, ignore intermediate hide() calls so the
    // overlay stays continuously visible across the master-list load sequence
    // (filters -> products -> preview generation). It is only actually hidden once
    // the persistent message has been cleared at the terminal point.
    if (this.persistentMessage) {
      return;
    }
    this.progressCurrent = null;
    this.progressTotal = null;
    this.loaderSubject.next(<LoaderState>{ show: false, message: '', subtitle: '' });
  }
  lazyLoaderShow() {
    this.lazyloaderSubject.next(<LazyLoaderState>{ lazyshow: true });
  }
  lazyLoaderHide() {
    this.lazyloaderSubject.next(<LazyLoaderState>{ lazyshow: false });
  }
}

export interface LoaderState {
  show: boolean;
  message?: string;
  subtitle?: string;
  progressCurrent?: number | null;
  progressTotal?: number | null;
}
export interface LazyLoaderState {
  lazyshow: boolean;
}
